import { Phone, Bot, TrendingUp, Zap, FileText, Building, Calculator, Users, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const SplashPage = () => {
  const features = [
    {
      icon: Phone,
      title: '24/7 AI Voice Agents',
      description: 'Human-like conversations. Real negotiation. Instant qualification.'
    },
    {
      icon: Bot,
      title: 'Autonomous Acquisition Bots',
      description: 'Run deal pipelines for Land, Wholesale, Section 8, and Multifamily'
    },
    {
      icon: TrendingUp,
      title: 'Instant Underwriting',
      description: 'AI analyzes deals, comps, and financials in seconds'
    },
    {
      icon: FileText,
      title: 'Auto Contract Generation',
      description: 'Once a seller agrees, REIPR generates and sends contracts automatically'
    },
    {
      icon: Building,
      title: 'Smart Disposition',
      description: 'AI markets your deals to builders, buyers, and investors'
    },
    {
      icon: Calculator,
      title: 'Portfolio & Tax Automation',
      description: 'Manage financials, KPIs, and tax reporting autonomously'
    }
  ];

  const assetClasses = [
    { name: 'Land Assignments', color: 'bg-green-500' },
    { name: 'Distressed SFH Wholesale', color: 'bg-blue-500' },
    { name: 'Section 8 Acquisitions', color: 'bg-orange-500' },
    { name: 'Multifamily Underwriting', color: 'bg-purple-500' }
  ];

  const howItWorks = [
    { step: '1', title: 'Voice AI Answers Every Call', desc: 'Human-like conversations. Real negotiation. Instant qualification.' },
    { step: '2', title: 'Smart Lead Scoring', desc: 'AI analyzes motivation, condition, timeline, and price to produce a deal score.' },
    { step: '3', title: 'Automated Outreach', desc: 'Voice → SMS → Email follow-ups — customizable per bot.' },
    { step: '4', title: 'Contract Generation', desc: 'Once a seller agrees, REIPR automatically generates and sends a contract.' },
    { step: '5', title: 'Disposition', desc: 'AI markets your deals to builders, buyers, and investors.' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-background"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight mb-6">
              Automate Real Estate Acquisition, Negotiation & Disposition
              <span className="block text-primary mt-2">with AI Voice Agents That Never Sleep</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              REIPR is the first end-to-end autonomous real estate acquisition platform.
              Search, qualify, negotiate, schedule, comp, assign, and close — across all asset classes.
            </p>
            <div className="flex gap-4 justify-center">
              <Button size="lg" className="text-lg px-8">
                <Zap className="h-5 w-5 mr-2" />
                Start Free Trial
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8">
                Book Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why REIPR Exists */}
      <section className="py-20 bg-card/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why REIPR Exists</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Investors lose deals because they respond too slowly, miss calls, can't qualify every lead, and don't follow up consistently.
            </p>
            <p className="text-2xl font-bold text-primary mt-4">REIPR fixes all of that.</p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Core Value Proposition</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <Card key={idx}>
                <CardHeader>
                  <feature.icon className="h-12 w-12 text-primary mb-4" />
                  <CardTitle>{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-card/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">How It Works</h2>
          </div>
          <div className="space-y-8 max-w-4xl mx-auto">
            {howItWorks.map((item, idx) => (
              <div key={idx} className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl">
                    {item.step}
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                  <p className="text-lg text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Asset Classes */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Supported Asset Classes</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {assetClasses.map((asset, idx) => (
              <Card key={idx} className="border-2">
                <CardContent className="p-6">
                  <div className={`h-16 w-16 rounded-full ${asset.color} mb-4 mx-auto flex items-center justify-center`}>
                    <CheckCircle className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-center">{asset.name}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold mb-6">
            Deploy Your First AI Acquisition Agent Today
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join the future of autonomous real estate investing
          </p>
          <Button size="lg" variant="secondary" className="text-lg px-8">
            Start Your Free Trial
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 text-center text-muted-foreground">
          <p>© 2025 REIPR. A product of KlearWater LLC.</p>
        </div>
      </footer>
    </div>
  );
};

export default SplashPage;